// import api's and mutations
import { mutations, queries } from 'RestAPIs/rapini';

// import atoms
import {
  planResponsePlanningEntityIdentifiersAtom,
  planUnitsAtom
} from '../../atoms';

// import components
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField
} from '@mui/material';
import { Dialog } from 'components/shared';
import { showSuccessSnackbar } from 'components/shared/Snackbar';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

// import hooks
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'usehooks-ts';
import { useRecoilState, useRecoilValue } from 'recoil';

// import types
import { AssignEmployeeDialogProps } from './AssignEmployeeDialog.types';

// import utils
import { getPlanUnitIds } from 'components/utils';
import { refetchPlanResponsePlanningEntityIdentifiers } from '../../utils';

export default function AssignEmployeeDialog({
  open,
  handleClose,
  appointmentGroupingEntityId,
  date,
  appointmentGroupingName,
  debounceInMS = 500
}: AssignEmployeeDialogProps) {
  const { t } = useTranslation('common');
  const selectedPlanUnits = useRecoilValue(planUnitsAtom);

  const [searchValue, setSearchValue] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(9);
  const [showInactiveEmployments, setShowInactiveEmployments] =
    useState<boolean>(false);
  const debouncedQuery = useDebounce(searchValue, debounceInMS);
  const {
    data: candidates,
    isFetching: isLoadingCandidates,
    refetch
  } = queries.useFindMatchCandidatesForAppointmentGrouping(
    appointmentGroupingEntityId,
    date,
    //@ts-expect-error TODO: BE should accept array of ids
    searchValue.length > 0
      ? undefined
      : getPlanUnitIds(selectedPlanUnits).join(','),
    searchValue.length > 0 ? searchValue : undefined,
    !showInactiveEmployments,
    {
      enabled: false
    }
  );

  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );

  const { mutate: assignEmployee, isLoading } =
    mutations.useAssignEmploymentToAppointmentGrouping(
      appointmentGroupingEntityId
    );

  function matches(text, partial) {
    return text.toLowerCase().indexOf(partial.toLowerCase()) > -1;
  }

  useEffect(() => {
    if (open) {
      setSearchValue('');
      refetch();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [debouncedQuery, showInactiveEmployments]);

  async function onAssign(data) {
    const splitData = data.split(';');
    await assignEmployee(
      {
        employeeNumber: splitData[0],
        employmentNumber: splitData[1],
        date
      },
      {
        onSuccess: () => {
          handleClose();
          showSuccessSnackbar();
          refetchPlanResponsePlanningEntityIdentifiers(
            setPlanResponsePlanningEntityIdentifiers
          );
        }
      }
    );
  }

  const columns = [
    {
      field: 'employeeNumber',
      headerName: t('employeeNr'),
      flex: 1,
      maxWidth: 100
    },
    {
      field: 'employeeName',
      headerName: t('name'),
      flex: 1,
      minWidth: 200
    },
    {
      field: 'employmentNumber',
      headerName: t('employment'),
      flex: 1,
      maxWidth: 100
    },
    {
      field: 'employmentFunction',
      headerName: t('function'),
      flex: 1,
      maxWidth: 200
    },
    {
      field: 'employmentOrganizationalUnit',
      headerName: t('organizationalUnit'),
      flex: 1,
      maxWidth: 200
    }
  ];
  const rows = useMemo(() => {
    return (
      candidates
        ?.filter(({ employeeName, employeeNumber }) => {
          return (
            matches(employeeName, searchValue) ||
            matches(employeeNumber.toString(), searchValue)
          );
        })
        .map(
          ({
            employeeNumber,
            employmentNumber,
            employeeName,
            overlapWithDutiesPercentage,
            matchPercentage,
            function: employeeFunction,
            organizationalUnit
          }) => ({
            id: employeeNumber + ';' + employmentNumber,
            employeeNumber,
            employeeName: employeeName ?? '',
            employmentNumber,
            overlapWithDutiesPercentage,
            matchPercentage,
            employmentFunction: employeeFunction?.description ?? '',
            employmentOrganizationalUnit: organizationalUnit?.name ?? ''
          })
        ) ?? []
    );
  }, [candidates, searchValue]);

  return (
    <Dialog
      open={open}
      maxWidth="extralarge"
      handleClose={handleClose}
      title={`Medewerker koppelen: ${appointmentGroupingName}`}
    >
      <Box sx={{ width: '768px', height: '650px' }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <TextField
            label={t('search')}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
          />

          <FormGroup>
            <FormControlLabel
              label={t('showInactiveEmployments')}
              checked={showInactiveEmployments}
              onChange={(_, isChecked: boolean) => {
                setShowInactiveEmployments(isChecked);
              }}
              control={<Checkbox />}
            />
          </FormGroup>
        </Box>

        <TableCRUDLayout
          containerHeight="auto"
          loading={isLoading || isLoadingCandidates}
          columns={columns}
          rows={rows}
          customActions={[
            {
              tooltipTitle: 'Koppelen',
              icon: 'link',
              onClick: onAssign
            }
          ]}
          pagination
          pageSize={pageSize}
          editable={false}
          pageSizeOptions={[10]}
          onPaginationModelChange={({ pageSize }) => setPageSize(pageSize)}
          translationKey={
            searchValue.length == 0
              ? t('noEmployeesFoundOnSelectedOrganizationalUnit')
              : t('noEmployeesFoundViaSearchString')
          }
        />
      </Box>
    </Dialog>
  );
}
